import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { FaRegCopy } from "react-icons/fa";
import styles from './styles/SnippetDetailPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member {
  user_id: number;
  username: string;
  email: string;
  display_name?: string;
}

interface SnippetDetail {
  snippet_id: number;
  snippet: string;
  status: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
  snippet_id?: string;
}

const SnippetDetailPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { member_id, snippet_id } = useParams<RouteParams>();
  const navigate = useNavigate();
  const [memberInfo, setMemberInfo] = useState<Member | null>(null);
  const [snippet, setSnippet] = useState<SnippetDetail | null>(null);
  const [editedContent, setEditedContent] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState<boolean>(false);

  useEffect(() => {
    if (snippet) {
      setEditedContent(snippet.snippet);
    }
  }, [snippet]);

  useEffect(() => {
    const fetchMemberInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch member details.");
          return;
        }

        const data = await response.json();
        setMemberInfo(data);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id) {
      fetchMemberInfo();
    }
  }, [member_id, token, selectedOrgID]);

  useEffect(() => {
    const fetchSnippet = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/snippets/${snippet_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch snippet.");
          return;
        }

        const data = await response.json();
        setSnippet(data);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (snippet_id) {
      fetchSnippet();
    }
  }, [snippet_id, token, selectedOrgID]);

  const handleCopyClick = async () => {
    if (editedContent) {
      try {
        await navigator.clipboard.writeText(editedContent);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        setError("Failed to copy to clipboard");
      }
    }
  };

  const handleSaveDraft = async () => {
    if (!snippet_id) return;
    
    setSaving(true);
    setSaveSuccess(false);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/snippets/${snippet_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({ snippet: editedContent }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to save snippet.");
        return;
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      setError("An unexpected error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!snippet_id || !member_id) return;
    
    setSending(true);
    setError("");

    try {
      // First, save the current content
      await handleSaveDraft();

      // Then, send the email
      const emailResponse = await fetch(`${API_BASE_URL}/api/snippets/${snippet_id}/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!emailResponse.ok) {
        const errorData = await emailResponse.json();
        setError(errorData.error || "Failed to send email.");
        return;
      }

      setShowEmailConfirm(false);
      navigate(`/members/${member_id}/snippets`);
    } catch (err) {
      setError("An unexpected error occurred while sending email.");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>View Snippet</h1>
      {memberInfo && (
        <p className={styles.memberInfo}>
          <strong>Member:</strong>{" "}
          <a href={`/members/${member_id}`} className={styles.memberLink}>
            {memberInfo.display_name 
              ? `${memberInfo.display_name} (${memberInfo.username})`
              : memberInfo.username}
          </a>
          {" / "}
          <a href={`/members/${member_id}/snippets`} className={styles.memberLink}>Snippets</a>
        </p>
      )}

      {snippet && (
        <div className={styles.contentSection}>
          <div className={styles.toolbar}>
            <button 
              className={`${styles.copyButton} ${copySuccess ? styles.success : ''}`}
              onClick={handleCopyClick}
              title="Copy to clipboard"
            >
              <FaRegCopy /> {copySuccess ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className={styles.content}>
            {snippet.status === "sent" ? (
              <div className={styles.previewContent} dangerouslySetInnerHTML={{ __html: editedContent }} />
            ) : (
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={20}
                className={styles.textarea}
              />
            )}
          </div>
          {snippet.status !== "sent" && (
            <div className={styles.actions}>
              <button
                onClick={() => setShowEmailConfirm(true)}
                disabled={sending}
                className={styles.emailButton}
              >
                {sending ? "Sending..." : "Send as Email"}
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                className={`${styles.saveButton} ${saveSuccess ? styles.success : ''}`}
              >
                {saving ? "Saving..." : saveSuccess ? "Saved!" : "Save Draft"}
              </button>
            </div>
          )}
        </div>
      )}

      {showEmailConfirm && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setShowEmailConfirm(false)} />
          <div className={styles.dialog}>
            <p>Email this Snippet to {memberInfo?.email}?</p>
            <div className={styles.previewContainer}>
              <div 
                className={styles.previewContent}
                dangerouslySetInnerHTML={{ __html: editedContent }}
              />
            </div>
            <div className={styles.dialogButtons}>
              <button onClick={handleSendEmail} disabled={sending} className={styles.dialogButton}>
                {sending ? "Sending..." : "Yes, Send"}
              </button>
              <button onClick={() => setShowEmailConfirm(false)} className={styles.dialogCancelButton}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SnippetDetailPage; 